import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Button,
  Typography,
  Chip,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded'
import FilterListIcon from '@mui/icons-material/FilterList'
import TableTemplate, { Column } from '../../../../Common/Table/TableTemplate'
import { RootState, AppDispatch } from '../../../../../redux/store'
import {
  selectFilteredAssignedTeamMembers,
  selectAssignedTeamMembersLoading,
  selectAssignedTeamMembersError,
} from '../../../../../redux/reports/task/assignedTeamMemberPerTask/selector'
import {
  setTaskStatusFilter,
  setSearchQuery,
  setFunctionalAreaFilter,
  setSupervisorNameFilter,
  setTidsPracticeFilter,
  generateReport,
  setOperationalManagerNameFilter,
} from '../../../../../redux/reports/task/assignedTeamMemberPerTask/slice'
import { fontSizes, fontWeights } from '../../../../Common/Typography/TypographyStyles'

const TeamMemberTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [refreshTable, _setRefreshTable] = useState(false)

  const filteredReport = useSelector(selectFilteredAssignedTeamMembers)
  const loading = useSelector(selectAssignedTeamMembersLoading)
  const error = useSelector(selectAssignedTeamMembersError)

  const searchQuery = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.searchQuery,
  )

  const activeStatusFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.taskStatus,
  )

  const activeFunctionalAreaFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.functionalArea,
  )

  const activeSupervisorFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.supervisorName,
  )

  const activeTidsPracticeFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.tidsPractice,
  )

  const activeOperationalManagerFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.operationalManagerName,
  )

  // Map filter value → human-readable label
  const STATUS_FILTER_LABELS: Record<string, string> = {
    completed: 'Completed',
    inprogress: 'In Progress',
    overdue: 'Overdue',
    notstarted: 'Not Started',
  }

  const activeStatusLabel = activeStatusFilter
    ? STATUS_FILTER_LABELS[activeStatusFilter.toLowerCase()] ?? activeStatusFilter
    : null

  const hasChartFilters = !!(
    activeStatusLabel ||
    activeTidsPracticeFilter ||
    activeOperationalManagerFilter ||
    activeFunctionalAreaFilter ||
    activeSupervisorFilter
  )

  const handleSearchChange = (value: string) => {
    dispatch(setSearchQuery(value))
  }

  const handleClearSearch = () => {
    dispatch(setSearchQuery(''))
  }

  const handleGenerateReport = () => {
    dispatch(generateReport())
  }

  const columns: Column[] = [
    {
      field: 'fullName',
      headerName: 'TEAM MEMBER DETAILS',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string, item: any) => (
        <>
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              fontSize: fontSizes.size12,
            }}
          >
            {item.email}
          </Typography>
        </>
      ),
    },
    {
      field: 'supervisorName',
      headerName: 'SUPERVISOR',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
    },
    {
      field: 'functionalArea',
      headerName: 'FUNCTIONAL AREA',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string) => (
        <Typography
          sx={{
            maxWidth: 200,
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            display: 'block',
            fontSize: fontSizes.size12,
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: 'operationalManagerName',
      headerName: 'OPERATIONAL MANAGER',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string) => (
        <Typography
          sx={{
            maxWidth: 200,
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            display: 'block',
            margin: '0 auto',
            fontSize: fontSizes.size12,
          }}
        >
          {value}
        </Typography>
      ),
    },

    {
      field: 'tidsPractice',
      headerName: 'TIDS PRACTICE',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (param: string) => (
        <div style={{ width: 200, whiteSpace: 'wrap' }}>
          <Box
            component='div'
            sx={{
              textOverflow: 'clip',
              overflow: 'hidden',
            }}
          >
            {param}
          </Box>
        </div>
      ),
    },
    {
      field: 'taskStatus',
      headerName: 'STATUS',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string) => {
        const normalized = value?.toLowerCase()
        if (normalized === 'completed') return 'Completed'
        if (normalized === 'inprogress' || normalized === 'in progress') return 'In Progress'
        return 'Not Started'
      },
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ m: 2 }}>
        {/* Search and Generate Report Grid */}
        <Box display='flex' justifyContent='flex-end' alignItems='center' gap={1.5} sx={{ mb: 2 }}>
          <FormControl sx={{ width: 260 }} variant='outlined' size='small'>
            <InputLabel htmlFor='search-input'>Search</InputLabel>
            <OutlinedInput
              id='search-input'
              value={searchQuery || ''}
              autoComplete='off'
              endAdornment={
                <InputAdornment position='end'>
                  {searchQuery ? (
                    <IconButton
                      edge='end'
                      onClick={handleClearSearch}
                      aria-label='clear search'
                      id='search-clear-button'
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : (
                    <SearchOutlinedIcon />
                  )}
                </InputAdornment>
              }
              label='Search'
              placeholder='Search by Name or Email'
              type='text'
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </FormControl>
          <Button
            variant='contained'
            color='primary'
            startIcon={<DownloadForOfflineRoundedIcon />}
            onClick={handleGenerateReport}
          >
            Export CSV
          </Button>
        </Box>

        {/* Active chart filter indicator */}
        {hasChartFilters && (
          <Box display='flex' alignItems='center' flexWrap='wrap' gap={1} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: fontSizes.size12, color: 'text.secondary' }}>
              Filtered by chart:
            </Typography>
            {activeStatusLabel && (
              <Chip
                icon={<FilterListIcon />}
                label={`Status: ${activeStatusLabel}`}
                onDelete={() => {
                  dispatch(setTaskStatusFilter(undefined))
                  dispatch(setTidsPracticeFilter(undefined))
                  dispatch(setOperationalManagerNameFilter(undefined))
                  dispatch(setFunctionalAreaFilter(undefined))
                  dispatch(setSupervisorNameFilter(undefined))
                }}
                color='default'
                size='small'
              />
            )}
            {activeTidsPracticeFilter && (
              <Chip
                icon={<FilterListIcon />}
                label={`TIDS Practice: ${activeTidsPracticeFilter}`}
                onDelete={() => {
                  dispatch(setTidsPracticeFilter(undefined))
                  dispatch(setOperationalManagerNameFilter(undefined))
                  dispatch(setFunctionalAreaFilter(undefined))
                  dispatch(setSupervisorNameFilter(undefined))
                }}
                color='default'
                size='small'
              />
            )}
            {activeOperationalManagerFilter && (
              <Chip
                icon={<FilterListIcon />}
                label={`Op. Manager: ${activeOperationalManagerFilter}`}
                onDelete={() => {
                  dispatch(setOperationalManagerNameFilter(undefined))
                  dispatch(setFunctionalAreaFilter(undefined))
                  dispatch(setSupervisorNameFilter(undefined))
                }}
                color='default'
                size='small'
              />
            )}
            {activeFunctionalAreaFilter && (
              <Chip
                icon={<FilterListIcon />}
                label={`FA: ${activeFunctionalAreaFilter}`}
                onDelete={() => {
                  dispatch(setFunctionalAreaFilter(undefined))
                  dispatch(setSupervisorNameFilter(undefined))
                }}
                color='default'
                size='small'
              />
            )}
            {activeSupervisorFilter && (
              <Chip
                icon={<FilterListIcon />}
                label={`Supervisor: ${activeSupervisorFilter}`}
                onDelete={() => dispatch(setSupervisorNameFilter(undefined))}
                color='default'
                size='small'
              />
            )}
          </Box>
        )}

      </Box>

      <TableTemplate
        columns={columns}
        datas={filteredReport?.assignedTeamMembers || []}
        refreshTable={refreshTable}
        state={{ loading, error }}
        uniqueField='workdayId'
      />
    </Box>
  )
}

export default TeamMemberTable
